/**
 * F-034 — First-time tutorial.
 *
 * Tracks whether the user has completed the 3-step intro (drag · launch
 * · merge). KVStorage persists the flag so returning players don't see
 * it again. UI is a stack of dismissable cards.
 */

import {
  createMemoryStorage,
  type KVStorage,
} from "../persistence/high-score-store.ts";
import { t } from "../i18n/index.ts";

const STORAGE_KEY = "cosmic-suika.tutorial.v1";

export interface TutorialStep {
  id: string;
  /** Lookup key for i18n — UI resolves via `t()` at render time. */
  titleKey: string;
  bodyKey: string;
  /** Resolved label for the active locale (computed at access time). */
  readonly title: string;
  readonly body: string;
}

interface StepDef {
  id: string;
  titleKey: string;
  bodyKey: string;
}

const STEP_DEFS: ReadonlyArray<StepDef> = [
  { id: "step-launch", titleKey: "tutorial.step.launch.title", bodyKey: "tutorial.step.launch.body" },
  { id: "step-merge",  titleKey: "tutorial.step.merge.title",  bodyKey: "tutorial.step.merge.body" },
  { id: "step-danger", titleKey: "tutorial.step.danger.title", bodyKey: "tutorial.step.danger.body" },
  { id: "step-goal",   titleKey: "tutorial.step.goal.title",   bodyKey: "tutorial.step.goal.body" },
];

function makeStep(def: StepDef): TutorialStep {
  return {
    id: def.id,
    titleKey: def.titleKey,
    bodyKey: def.bodyKey,
    get title() {
      return t(def.titleKey);
    },
    get body() {
      return t(def.bodyKey);
    },
  };
}

export const TUTORIAL_STEPS: ReadonlyArray<TutorialStep> = STEP_DEFS.map(makeStep);

export interface TutorialController {
  isShown(): boolean;
  shouldShow(): boolean;
  show(): void;
  next(): TutorialStep | null;
  skip(): void;
  reset(): void;
  currentStep(): TutorialStep | null;
}

export interface TutorialOptions {
  storage?: KVStorage;
  storageKey?: string;
  steps?: ReadonlyArray<TutorialStep>;
}

function defaultStorage(): KVStorage {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    return window.localStorage;
  } catch {
    return createMemoryStorage();
  }
}

export function createTutorialController(
  options: TutorialOptions = {},
): TutorialController {
  const storage = options.storage ?? defaultStorage();
  const key = options.storageKey ?? STORAGE_KEY;
  const steps = options.steps ?? TUTORIAL_STEPS;

  let stepIndex = -1; // -1 = not started, length = completed
  let seen = storage.getItem(key) === "1";

  const persist = (): void => {
    try {
      storage.setItem(key, seen ? "1" : "0");
    } catch {
      /* quota — silent */
    }
  };

  const show = (): void => {
    stepIndex = 0;
  };

  const next = (): TutorialStep | null => {
    if (stepIndex < 0) {
      stepIndex = 0;
      return steps[stepIndex] ?? null;
    }
    stepIndex += 1;
    if (stepIndex >= steps.length) {
      seen = true;
      persist();
      return null;
    }
    return steps[stepIndex] ?? null;
  };

  const skip = (): void => {
    seen = true;
    stepIndex = steps.length;
    persist();
  };

  const reset = (): void => {
    seen = false;
    stepIndex = -1;
    persist();
  };

  const currentStep = (): TutorialStep | null => {
    if (stepIndex < 0 || stepIndex >= steps.length) return null;
    return steps[stepIndex] ?? null;
  };

  return {
    isShown: () => stepIndex >= 0 && stepIndex < steps.length,
    shouldShow: () => !seen,
    show,
    next,
    skip,
    reset,
    currentStep,
  };
}

export const __testing = { STORAGE_KEY };
